
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (searchParams.get('success') === 'contact') {
      setShowSuccessMessage(true);
      // Rimuovi il parametro dall'URL
      setSearchParams({});
      // Nascondi il messaggio dopo 5 secondi
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-screen relative">
      {/* 3D Globe Background for entire page */}
      <div className="fixed inset-0 z-0">
        <iframe 
          src='https://my.spline.design/3dglobe-p3XQBgXXJW2L7fmP5LlHN6Xc/' 
          frameBorder='0' 
          width='100%' 
          height='100%'
          className="w-full h-full"
        />
      </div>
      
      {/* Overlay for better content readability */}
      <div className="fixed inset-0 bg-black/40 z-10"></div>
      
      {/* All page content */}
      <div className="relative z-20">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center space-x-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-check-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold">Messaggio inviato con successo!</p>
              <p className="text-sm opacity-90">Ti risponderemo al più presto.</p>
            </div>
            <button
              onClick={() => setShowSuccessMessage(false)}
              className="w-6 h-6 flex items-center justify-center hover:bg-green-600 rounded"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center relative">
          <div className="container mx-auto px-6 text-center text-white relative z-10">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-2xl">
              Transform Your
              <span className="block text-blue-400">Digital Ideas</span>
              Into Reality
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
              Professional web development, innovative design and custom digital solutions 
              to grow your online business
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold shadow-2xl"
              >
                Start Your Project
              </Button>
              <Button 
                onClick={() => navigate('/services')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-semibold shadow-2xl"
              >
                Discover Services
              </Button>
            </div>
          </div>
        </section>

        {/* About Section - Mission */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6 drop-shadow-2xl">
                Driving Sustainable Digital Growth
              </h2>
              <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed drop-shadow-lg">
                We deliver a cohesive digital platform that produces measurable results and sustainable growth. 
                We combine strategy, technology and execution to scale businesses from 1X to 100X.
              </p>
            </div>

            {/* Results Section */}
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 mb-16 border border-white/20 shadow-2xl">
              <h3 className="text-3xl font-bold text-center text-white mb-12 drop-shadow-lg">Let the Results Speak</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2 drop-shadow-lg">500+</div>
                  <div className="text-white/80 drop-shadow-md">Global Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-400 mb-2 drop-shadow-lg">300%</div>
                  <div className="text-white/80 drop-shadow-md">Average ROI</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-400 mb-2 drop-shadow-lg">99.9%</div>
                  <div className="text-white/80 drop-shadow-md">System Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-400 mb-2 drop-shadow-lg">24/7</div>
                  <div className="text-white/80 drop-shadow-md">Support Available</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6 drop-shadow-2xl">Our Services</h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow-lg">
                Comprehensive digital solutions across all business verticals
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Marketing */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl hover:bg-white/15 transition-all duration-300 cursor-pointer border border-white/20">
                <div className="w-16 h-16 bg-blue-500/20 backdrop-blur-md rounded-full flex items-center justify-center mb-6 border border-blue-300/30">
                  <i className="ri-megaphone-line text-2xl text-blue-300"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-lg">Marketing</h3>
                <p className="text-white/80 mb-4 drop-shadow-md">SEO, Paid Search & SEM, PPC, Content, Social, Email</p>
                <a href="/services" className="text-blue-300 font-semibold hover:text-blue-200 cursor-pointer drop-shadow-md">
                  Learn More →
                </a>
              </div>

              {/* AI Integration */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl hover:bg-white/15 transition-all duration-300 cursor-pointer border border-white/20">
                <div className="w-16 h-16 bg-purple-500/20 backdrop-blur-md rounded-full flex items-center justify-center mb-6 border border-purple-300/30">
                  <i className="ri-robot-line text-2xl text-purple-300"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-lg">AI Integration</h3>
                <p className="text-white/80 mb-4 drop-shadow-md">Chatbots, Content Generation, Code Assessment, Machine Learning</p>
                <a href="/services" className="text-purple-300 font-semibold hover:text-purple-200 cursor-pointer drop-shadow-md">
                  Learn More →
                </a>
              </div>

              {/* Reputation Management */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl hover:bg-white/15 transition-all duration-300 cursor-pointer border border-white/20">
                <div className="w-16 h-16 bg-yellow-500/20 backdrop-blur-md rounded-full flex items-center justify-center mb-6 border border-yellow-300/30">
                  <i className="ri-star-line text-2xl text-yellow-300"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-lg">Reputation</h3>
                <p className="text-white/80 mb-4 drop-shadow-md">Negative Content Removal, Deindexing, Crisis Management</p>
                <a href="/services" className="text-yellow-300 font-semibold hover:text-yellow-200 cursor-pointer drop-shadow-md">
                  Learn More →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6 drop-shadow-2xl">Why Choose Us</h2>
              <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed drop-shadow-lg">
                Integrated end-to-end digital ecosystem — not a single-focus agency. 
                Benefits: seamless cross-discipline strategy, proprietary AI tools, global expertise, measurable ROI.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-blue-300/30">
                  <i className="ri-links-line text-3xl text-blue-300"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 drop-shadow-lg">Fully Integrated Services</h3>
                <p className="text-white/80 drop-shadow-md">Seamless coordination across all digital channels</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-purple-500/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-purple-300/30">
                  <i className="ri-team-line text-3xl text-purple-300"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 drop-shadow-lg">Multidisciplinary Team</h3>
                <p className="text-white/80 drop-shadow-md">Experts across marketing, tech, and business strategy</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-green-500/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-green-300/30">
                  <i className="ri-line-chart-line text-3xl text-green-300"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 drop-shadow-lg">Scalable Outcomes</h3>
                <p className="text-white/80 drop-shadow-md">Measurable results that grow with your business</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-orange-500/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6 border border-orange-300/30">
                  <i className="ri-global-line text-3xl text-orange-300"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 drop-shadow-lg">Global Reach</h3>
                <p className="text-white/80 drop-shadow-md">Worldwide expertise with local market execution</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto text-center px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-2xl">
              Ready to Scale Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8 drop-shadow-lg">
              Get your personalized digital growth plan and start transforming your business today.
            </p>
            <a 
              href="/contact" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer inline-block"
            >
              Get My Digital Growth Plan
            </a>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;
